import { useEffect, useMemo, useRef, useState } from "react";
import { DatePicker, Form, Switch } from "antd";
import moment from "moment-timezone";
import { Drawer } from "../../../../components/drawer/drawer";
import { DrawerHeader } from "../../../../components/drawer/drawer-header";
import { FormSelect } from "../../../../components/form/form";
import ApiTaskManagement from "../../../../api/task-managements";
import useNotification from "../../../../components/notification/notification";
import ReminderList from "../../../../components/reminder-time/reminder-list";
import { earliestTimeToday } from "../../../../components/reminder-time/earliest-time-today";
import {
  remindersForPersonalTask,
  rememberReminderTypeDraft,
  remindersToPayload,
} from "../../../administration/plan-management/components/reminder-mapping";
import ApiProfileManagement from "../../../../api/profile-management";
import ApiSubjectsManager from "../../../../api/subjects-manager";
import { triggerFieldOptions } from "../../../administration/plan-management/components/trigger-field-options";
import "../styles.css";

// The API reports errors either as a string or as { code }; rendering the raw
// value would crash on the object form.
const errorTextOf = (err, fallback) => {
  const value = err?.response?.data?.error;
  if (typeof value === "string" && value) return value;
  if (value && typeof value.code === "string") return value.code;
  return fallback;
};

const DURATION_OPTIONS = Array.from({ length: 99 }, (_, i) => ({
  value: i + 1,
  label: String(i + 1),
}));

// Portal-style drawer for manually attaching a DailyTask to a subject for a
// specific local date. Layout mirrors article-modal / plan-default-data-modal:
// shared <Drawer> + <DrawerHeader> shell, antd Form with label-left rows.
const AssignTaskDrawer = ({ open, onClose, subjectId, timezone, onAssigned }) => {
  const tz = timezone || "UTC";
  const { openNotification } = useNotification();
  const [form] = Form.useForm();

  const [tasks, setTasks] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [duration, setDuration] = useState(1);
  // LF360-1830: additional reminders for this assignment.
  const [reminders, setReminders] = useState([]);
  // LF360-1940: whether these are this assignment's own reminders or the ones
  // the chosen task carries. A fresh assignment follows its task until somebody
  // changes something here.
  const [remindersCustomised, setRemindersCustomised] = useState(false);
  // LF360-1830: a personal task can be relative — its time comes from one of the
  // subject's profile time fields plus an offset, like weekly/triggered tasks.
  const [notificationType, setNotificationType] = useState("absolute");
  const [timeProfileFields, setTimeProfileFields] = useState([]);
  // The subject's own resolved values: a time field they have never answered
  // cannot schedule anything, and the backend refuses such an assignment.
  const [subjectCustomFields, setSubjectCustomFields] = useState(null);
  const reminderTypeDrafts = useRef(new Map());

  // Watched rather than read: the constraint has to follow the date the admin
  // picks, and getFieldValue would only ever report what it was on first render.
  // The rule itself is shared with the other drawer and counted in the subject's
  // timezone, which is the one the backend counts in.
  const chosenDate = Form.useWatch("date", form);
  const minMinutes = useMemo(
    () => earliestTimeToday(chosenDate, tz),
    [chosenDate, tz]
  );

  // Picking a task brings its reminders with it, and that is the state a new
  // assignment starts in — following, with nothing yet chosen here.
  //
  // Including one that has already gone today. Tasks are set up with a morning
  // default, so an afternoon assignment inherits a time that has passed; it is
  // kept as the task has it and simply does not send today. Moving it to a free
  // slot instead was tried and taken back out: nobody chose the new time, it
  // would apply to every later day of a multi-day task as well, and it detached
  // the assignment from the task for good. An admin who wants a reminder today
  // can add one, which is a choice they made.
  const takeFromTask = (picked) => {
    setRemindersCustomised(false);
    setReminders(remindersForPersonalTask(picked?.defaultReminders));
  };

  const handleTaskChange = (nextTaskId) => {
    takeFromTask(tasks.find((one) => one.id === nextTaskId));
  };

  const handleResetReminders = () => {
    const chosen = form.getFieldValue("dailyTaskId");

    // Reachable with no task chosen: pick one, edit a reminder so the undo
    // appears, then clear the select. Going back to the reminders of a task
    // that is not there would empty the list and call it the task's answer.
    if (!chosen) { return; }

    handleTaskChange(chosen);
  };

  // Editing them here is what makes them this assignment's own.
  const handleRemindersChange = (next) => {
    setRemindersCustomised(true);
    rememberReminderTypeDraft(
      reminderTypeDrafts.current,
      "assignment",
      notificationType,
      next
    );
    setReminders(next);
  };

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    ApiProfileManagement.getProfiles({ inputType: ["message:time"] })
      .then(({ data }) => {
        if (!cancelled) setTimeProfileFields(data?.subjectProfileFields || []);
      })
      .catch(() => {
        if (!cancelled) setTimeProfileFields([]);
      });
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open || !subjectId) return undefined;
    // Guarded: a reply for the previous subject must not decide which trigger
    // fields this one may use.
    let cancelled = false;
    setSubjectCustomFields(null);
    ApiSubjectsManager.getSubject(subjectId)
      .then(({ data }) => {
        if (!cancelled) setSubjectCustomFields(data?.subject?.customFields || []);
      })
      // Left null on failure, so nothing is disabled on incomplete information.
      .catch(() => {
        if (!cancelled) setSubjectCustomFields(null);
      });
    return () => { cancelled = true; };
  }, [open, subjectId]);
  const [rescheduling, setRescheduling] = useState(true);

  useEffect(() => {
    if (!open) return;

    ApiTaskManagement.getActiveTasks()
      .then(({ data }) => setTasks(data?.taskManagements || []))
      .catch(() => setTasks([]));
  }, [open]);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setDuration(1);
      setRescheduling(true);
      // The reminder configuration is subject-specific too: left behind, it would
      // reappear on the next open, possibly for a different subject (LF360-1830).
      setReminders([]);
      setRemindersCustomised(false);
      setNotificationType("absolute");
      setSubjectCustomFields(null);
      reminderTypeDrafts.current.clear();
    }
  }, [open, form]);

  const taskOptions = useMemo(
    () =>
      tasks.map((t) => ({
        value: t.id,
        label: t.title,
      })),
    [tasks]
  );

  const todayKey = useMemo(() => moment.tz(tz).format("YYYY-MM-DD"), [tz]);

  // LF360-1940: assigning for today, the backend refuses a reminder that has
  // already passed. The picker is told the same rule so the hours before now are
  // simply not selectable, rather than the admin finding out on Save.

  const disabledDate = (current) =>
    current && current.format("YYYY-MM-DD") < todayKey;

  // For TimePicker: if the picked date is the subject's "today", mask out
  // hours/minutes that already passed in the subject's local time. The
  // backend re-validates this, so both layers agree.
  const handleDateChange = (next) => {

    // If switching back to today wipes out an already-picked future time, drop
    // the now-invalid value so the user has to pick again rather than submit
    // a time the backend will reject.
    if (next && next.format("YYYY-MM-DD") === todayKey) {
      const time = form.getFieldValue("time");
      if (time) {
        const now = moment.tz(tz);
        const composed = next
          .hour(time.hour())
          .minute(time.minute())
          .second(0);
        if (composed.valueOf() <= now.valueOf()) {
          form.setFieldValue("time", null);
        }
      }
    }
  };

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (_e) {
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        dailyTaskId: values.dailyTaskId,
        date: values.date.format("YYYY-MM-DD"),
        duration,
        // Multi-day tasks can't be rescheduled by the subject (same rule as
        // plan-management). Backend re-enforces this, but we also send a
        // sanitized value to keep the round-trip consistent.
        rescheduling: duration > 1 ? false : rescheduling,
      };
      // The reminder list carries the time: a task has a day, and its time is
      // simply its first reminder (LF360-1830).
      Object.assign(payload, remindersToPayload(reminders, notificationType));
      // LF360-1940: says whose these are, so the backend knows whether to take
      // the task's own instead.
      payload.remindersCustomised = remindersCustomised;

      await ApiTaskManagement.assignSubjectTask(subjectId, payload);
      openNotification({
        type: "success",
        message: <p>Task assigned</p>,
      });
      onAssigned && onAssigned();
      onClose && onClose();
    } catch (err) {
      openNotification({
        type: "error",
        message: <p>{errorTextOf(err, "Failed to assign task")}</p>,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer open={open} onClose={onClose} destroyOnHidden size={680}>
      <DrawerHeader
        title="Assign task"
        isEditing
        onCancel={onClose}
        onSave={handleSubmit}
        saveLabel="Assign"
        saveTestID="assign-task-save"
        isLoading={submitting}
      />

      <Form
        form={form}
        layout="horizontal"
        labelCol={{ flex: "160px" }}
        wrapperCol={{ flex: "auto" }}
        labelAlign="left"
        colon={false}
        requiredMark={false}
        style={{ marginTop: 8 }}
      >
        <Form.Item
          name="dailyTaskId"
          label="Task"
          rules={[{ required: true, message: "Pick a task" }]}
        >
          <FormSelect
            data-testid="assign-task-select"
            placeholder="Select task"
            options={taskOptions}
            onChange={handleTaskChange}
            showSearch
            optionFilterProp="label"
            filterOption={(input, option) =>
              (option?.label || "")
                .toLowerCase()
                .includes((input || "").toLowerCase())
            }
            getPopupContainer={(trigger) => trigger.parentNode}
          />
        </Form.Item>

        <Form.Item
          name="date"
          label="Date"
          rules={[{ required: true, message: "Pick a date" }]}
        >
          <DatePicker
            data-testid="assign-task-date"
            disabledDate={disabledDate}
            onChange={handleDateChange}
            format="DD MMM YYYY"
            allowClear={false}
            variant="borderless"
            style={{ width: 140 }}
          />
        </Form.Item>

        {/* LF360-1940: the same control the plan panels use. Each reminder's
            kind — a time of day or an offset from a profile answer — is chosen
            inside the picker, so this form no longer asks for a notification
            type or a trigger field of its own.

            A personally assigned task has no date before its own day, so the
            backend refuses a "before assignment" reminder: none is offered. */}
        <Form.Item label={remindersCustomised ? "Reminders" : "Reminders (default)"}>
          <div className="assigned-task-reminders">
            <ReminderList
              value={reminders}
              onChange={handleRemindersChange}
              triggerFields={triggerFieldOptions(timeProfileFields, subjectCustomFields)}
              minMinutes={minMinutes}
            />

            {/* Under the button that adds one, not beside the list. */}
            {remindersCustomised && (
              <button
                type="button"
                className="assigned-task-reset"
                onClick={handleResetReminders}
              >
                Reset to default
              </button>
            )}
          </div>
        </Form.Item>

        <Form.Item label="Duration">
          <FormSelect
            value={duration}
            options={DURATION_OPTIONS}
            onSelect={(value) => {
              setDuration(value);
              if (value > 1) setRescheduling(false);
            }}
            style={{ width: 100 }}
            getPopupContainer={(trigger) => trigger.parentNode}
          />
        </Form.Item>

        <Form.Item label="Reschedule">
          <Switch
            size="small"
            checked={duration > 1 ? false : rescheduling}
            disabled={duration > 1}
            onChange={setRescheduling}
          />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default AssignTaskDrawer;
